import React from 'react';

import { Box } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { useLoadProfitabilityData } from 'api/CompeonReverseApi/operation/queryHooks';
import { translateTargetMet } from 'mittweida/portals/operations/pages/FinancingCriteria/targetMetTranslator';
import { getInquiryIdSelector } from 'store/inquiryDetails/selectors';

import { getMarketDataFilterLegacy } from '../../../../../pages/operationPortal/PlanningEvaluation/SharedComponent/filterManagementReducer';
import { useTranslations } from '../../../../../utils/hooks/useTranslations';
import { replaceDotWithCommaAndAddUnit } from '../../../../../utils/valueFormats';
import { Card } from '../../components/Card';
import PortalTable from '../../components/PortalTable';
import usePortalTable from '../../hooks/usePortalTable';

interface FinancialCriteriaPartitionItem {
  value: string;
  target?: string;
  target_met: boolean | null;
}

export interface FinancialCriteriaPartition {
  equity_buying_costs_percent: FinancialCriteriaPartitionItem;
  margin_percent: FinancialCriteriaPartitionItem;
  warning_limit_unsecured_portion: FinancialCriteriaPartitionItem;
}

type FinancialCriteriaPartitionKey = keyof FinancialCriteriaPartition;

interface FinancialCriteriaTableData {
  id: string;
  label: string;
  value: string;
  target?: string;
  target_met: string;
}

const orderArray = [
  'equity_buying_costs_percent',
  'margin_percent',
  'warning_limit_unsecured_portion',
];

const useFinancialCriteriaPartitionMapping = (objectData: FinancialCriteriaPartition) => {
  const t = useTranslations('pages.planningEvaluation.mittweida.pages.financingCriteria');
  const mappedArray: Array<FinancialCriteriaTableData> = Object.keys(objectData).map((key) => {
    const unit = '%';
    return {
      id: key,
      label: t(`tables.financingCriteriaTable.rowNames.partition.${key}`),
      value: replaceDotWithCommaAndAddUnit(
        objectData[key as FinancialCriteriaPartitionKey].value,
        unit,
      ),
      target: replaceDotWithCommaAndAddUnit(
        objectData[key as FinancialCriteriaPartitionKey].target,
        unit,
      ),
      target_met: translateTargetMet(
        objectData[key as FinancialCriteriaPartitionKey].target_met,
        t,
      ),
    };
  });

  return orderArray.map((item) => {
    return mappedArray.find((object) => {
      if (object.id === item) {
        return object;
      }
      return null;
    });
  });
};

const FinancingCriteriaPropertyPartition = () => {
  const inquiryId = useSelector(getInquiryIdSelector) || '';
  const t = useTranslations(
    'pages.planningEvaluation.mittweida.pages.financingCriteria.tables.financingCriteriaTable',
  );
  const { data } = useLoadProfitabilityData({
    variables: { inquiryId },
    select: (response) => ({
      computedFinancingCriteria: response.data.attributes.computed_details.financing_criteria,
    }),
  });
  const filterData = useSelector(getMarketDataFilterLegacy);

  const financialCriteriaDataPartition = {
    current: data?.computedFinancingCriteria.current?.partitioner as FinancialCriteriaPartition,
    future: data?.computedFinancingCriteria.future?.partitioner as FinancialCriteriaPartition,
  };

  const tableData = useFinancialCriteriaPartitionMapping(
    financialCriteriaDataPartition?.[filterData?.usageType],
  );

  const financingCriteriaTable = usePortalTable({
    data: tableData,
    headers: [
      { key: 'label', text: '' },
      { key: 'target', text: t('columnNames.target') },
      { key: 'value', text: t('columnNames.value') },
      { key: 'target_met', text: t('columnNames.targetMet') },
    ],
  });

  return (
    <Box paddingTop={10}>
      <Card title={t('title.partition')}>
        <PortalTable
          {...financingCriteriaTable}
          headerStylesMap={{
            value: { width: '10%' },
            target: { width: '10%' },
            label: { width: '40%' },
            target_met: { width: '40%' },
          }}
        />
      </Card>
    </Box>
  );
};

export default FinancingCriteriaPropertyPartition;
